import React, { useState, useEffect } from 'react';
import { FiX, FiCalendar } from 'react-icons/fi';
import { subscriptionsAPI } from '../../services/api';

const AssignSubscriptionModal = ({ isOpen, onClose, establishment, onSuccess }) => {
  const [formData, setFormData] = useState({
    establishment_id: '',
    plan: 'basic',
    period: 'monthly',
    is_trial: true,
    trial_days: 30,
    notes: ''
  });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (establishment) {
      setFormData(prev => ({
        ...prev,
        establishment_id: establishment.id
      }));
    }
  }, [establishment]);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      const plansData = await subscriptionsAPI.getPlans({ include_inactive: false });
      setPlans(plansData);
    } catch (err) {
      console.error('Error loading plans:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Preparar datos según el tipo de suscripción
      const dataToSend = {
        establishment_id: formData.establishment_id,
        plan: formData.plan,
        period: formData.period,
        is_trial: formData.is_trial,
        notes: formData.notes
      };

      // Solo incluir trial_days si es trial
      if (formData.is_trial) {
        dataToSend.trial_days = formData.trial_days;
      }

      await subscriptionsAPI.assignSubscription(dataToSend);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al asignar la suscripción');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      establishment_id: '',
      plan: 'basic',
      period: 'monthly',
      is_trial: true,
      trial_days: 30,
      notes: ''
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Asignar Suscripción
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {establishment?.name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Tipo de Suscripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Suscripción
            </label>
            <select
              value={formData.is_trial ? 'trial' : 'paid'}
              onChange={(e) => setFormData({
                ...formData,
                is_trial: e.target.value === 'trial',
                // Resetear trial_days si cambia a paid
                trial_days: e.target.value === 'trial' ? formData.trial_days : 30
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="trial">Período de Prueba</option>
              <option value="paid">Licencia Pagada</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan
            </label>
            <select
              value={formData.plan}
              onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="basic">Básico</option>
              <option value="professional">Profesional</option>
              <option value="enterprise">Empresa</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Período de Facturación
            </label>
            <select
              value={formData.period}
              onChange={(e) => setFormData({ ...formData, period: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="monthly">Mensual</option>
              <option value="annual">Anual</option>
            </select>
          </div>

          {/* Mostrar campo de duración solo si es trial */}
          {formData.is_trial && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duración del Período de Prueba (días)
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.trial_days}
                  onChange={(e) => setFormData({ ...formData, trial_days: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Entre 1 y 365 días
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas (opcional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Observaciones sobre esta suscripción..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Asignando...' : 'Asignar Suscripción'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignSubscriptionModal;
